/**
 * Aturan gerak orb: kapan dianimasikan, seberapa cepat, dan bagaimana ia
 * meredup saat tak ada interaksi.
 *
 * Peredupan dan pergeseran halus di sini bukan sekadar hemat daya: kiosk
 * `/wall` menyala 24 jam dan jenis panel TV-nya belum dikonfirmasi, jadi
 * dirancang seolah OLED — titik terang yang tidak pernah berubah bisa
 * meninggalkan bekas permanen. Lihat ORB-LOGO-PROPOSAL.md §8.1.1.
 *
 * Fungsi murni tanpa DOM — diuji langsung dengan `bun:test`.
 */

import {
	ORB_FPS_LARGE,
	ORB_FPS_SMALL,
	ORB_IDLE_BRIGHTNESS,
	ORB_IDLE_DIM_AFTER_MS,
	ORB_IDLE_FADE_MS,
	ORB_IDLE_FREEZE_AFTER_MS,
	ORB_LARGE_SIZE_PX,
	ORB_PIXEL_SHIFT_PERIOD_MS,
	ORB_PIXEL_SHIFT_RADIUS_PX,
} from "./orb.config";

/**
 * Orb diam bila OS meminta gerak dikurangi atau setelan animasi aplikasi mati.
 * Menghormati setelan OS (prefers-reduced-motion) dan setelan aplikasi
 * sekaligus, seperti perilaku logo Jenna sebelumnya.
 */
export function shouldAnimateOrb(
	reducedMotion: boolean,
	animationsOn: boolean,
): boolean {
	return !reducedMotion && animationsOn;
}

/** Pratinjau besar boleh 60 fps; ikon kecil cukup 30 fps dan separuh bebannya. */
export function targetFps(cssSize: number): number {
	return cssSize >= ORB_LARGE_SIZE_PX ? ORB_FPS_LARGE : ORB_FPS_SMALL;
}

/**
 * Kecerahan orb (0–1) setelah `idleMs` tanpa interaksi. Turun bertahap, bukan
 * mendadak, supaya operator NOC tidak menangkapnya sebagai layar mati.
 */
export function idleBrightness(idleMs: number): number {
	if (!Number.isFinite(idleMs) || idleMs <= ORB_IDLE_DIM_AFTER_MS) return 1;
	const progress = Math.min(
		1,
		(idleMs - ORB_IDLE_DIM_AFTER_MS) / ORB_IDLE_FADE_MS,
	);
	return 1 - (1 - ORB_IDLE_BRIGHTNESS) * progress;
}

/** Setelah idle sangat lama, animasi berhenti pada satu frame redup. */
export function isIdleFrozen(idleMs: number): boolean {
	return Number.isFinite(idleMs) && idleMs >= ORB_IDLE_FREEZE_AFTER_MS;
}

export interface Offset {
	x: number;
	y: number;
}

/**
 * Pergeseran halus posisi orb (pixel shifting) — teknik yang dipakai TV OLED
 * sendiri. Radiusnya beberapa piksel dengan periode menit, jadi tidak terlihat
 * mata, tapi cukup agar satu piksel tidak menanggung beban terang terus.
 */
export function pixelShift(elapsedMs: number): Offset {
	if (!Number.isFinite(elapsedMs)) return { x: 0, y: 0 };
	const angle = (elapsedMs / ORB_PIXEL_SHIFT_PERIOD_MS) * Math.PI * 2;
	return {
		x: Math.cos(angle) * ORB_PIXEL_SHIFT_RADIUS_PX,
		y: Math.sin(angle) * ORB_PIXEL_SHIFT_RADIUS_PX,
	};
}
